import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { Users, Target, Shield, MessageSquare, Handshake } from 'lucide-react';

const features = [
  { icon: Users,         title: 'Expert Engineering Team',      description: 'Seasoned developers and architects with years of enterprise-grade delivery experience.' },
  { icon: Target,        title: 'Business-First Approach',      description: 'We understand your goals first, then craft technology that delivers real, measurable ROI.' },
  { icon: Shield,        title: 'Scalable & Secure',            description: 'Security and scalability built into the architecture from day one — not bolted on later.' },
  { icon: MessageSquare, title: 'Transparent Communication',    description: 'Regular updates, clear timelines, and honest feedback throughout every project.' },
  { icon: Handshake,     title: 'Long-term Technology Partner', description: "We're not just a vendor — we're your strategic partner committed to your long-term success." },
];

const stats = [
  { value: 100, suffix: '%',   label: 'Project Success' },
  { value: 24,  suffix: '/7',  label: 'Support Available' },
  { value: 5,   suffix: '+',   label: 'Years Expertise' },
];

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v));

  useEffect(() => {
    if (inView) {
      const controls = animate(count, to, { duration: 1.6, ease: [0.22, 1, 0.36, 1] });
      return () => controls.stop();
    }
  }, [inView, to, count]);

  return (
    <span ref={ref} className="font-display text-4xl font-bold text-[#2563EB]">
      <motion.span>{rounded}</motion.span>{suffix}
    </span>
  );
};

export const WhyUsSection = () => {
  return (
    <section id="why-us" className="section-padding bg-white relative overflow-hidden" data-testid="why-us-section">
      <div className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full bg-[#3B82F6]/[0.04] blur-[100px] pointer-events-none" />

      <div className="container-custom relative">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">

          {/* LEFT */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="section-label">Why Choose Us</span>
              <span className="w-6 h-px bg-[#2563EB]/30" />
            </div>
            <h2
              className="font-display font-bold text-[#0F172A] mb-8"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
            >
              Built on <span className="italic text-gold">Trust</span>,<br />
              Driven by Results
            </h2>
            <p className="text-[#374151] leading-relaxed mb-12 max-w-md">
              We combine technical excellence with deep business understanding to deliver
              solutions that make a measurable impact on your operations and growth.
            </p>

            {/* Animated counter stats */}
            <div className="grid grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
              {stats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.1 }}
                  className="bg-white p-6 text-center group hover:bg-[#FAFAF7] transition-colors duration-300 cursor-default relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#2563EB] to-[#3B82F6] scale-x-0 group-hover:scale-x-100 transition-transform duration-400 origin-left" />
                  <div className="mb-2">
                    <Counter to={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="font-mono text-[0.58rem] tracking-widest text-[#374151] uppercase">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-14 border-l-2 border-[#2563EB]/40 pl-6">
              <p className="font-display italic text-[#374151] text-base leading-relaxed">
                "Technology is most powerful when it solves real business problems."
              </p>
            </div>
          </motion.div>

          {/* RIGHT — Feature list */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="border border-[#E2E8F0] bg-white"
          >
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  whileHover={{ x: 4 }}
                  className="group flex gap-5 p-6 border-b border-[#E2E8F0] last:border-0 hover:bg-[#FAFAF7] transition-colors duration-300 relative overflow-hidden cursor-default"
                  data-testid={`feature-card-${i}`}
                >
                  <div className="absolute left-0 top-5 bottom-5 w-[2px] bg-[#2563EB] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-400" />

                  <motion.div
                    whileHover={{ rotate: -4, scale: 1.05 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 14 }}
                    className="w-10 h-10 flex items-center justify-center shrink-0 mt-0.5 border border-[#E2E8F0] bg-[#F6F9FC] group-hover:border-[#2563EB] group-hover:bg-[#2563EB] transition-all duration-300"
                  >
                    <Icon size={17} className="text-[#2563EB] group-hover:text-[#3B82F6] transition-colors duration-300" />
                  </motion.div>
                  <div className="flex-1">
                    <h3 className="text-[#0F172A] font-semibold text-[0.95rem] mb-1.5 group-hover:text-[#2563EB] transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-[#374151] text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

        </div>
      </div>
    </section>
  );
};
